import {
  type Env,
  PostOfficeProvider as PostOfficeProviderBase,
  type PostOfficeContextConfig,
  type PostOfficeEnvironmentConfig,
} from '@atlassian/post-office-context';
import React, { createContext, useContext, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import { currentOrgData } from '@canvas/canvas-state/org-data';
import { currentSiteData } from '@canvas/canvas-state/site-data';
import { useUserLocale } from '@canvas/canvas-state/use-user-locale';

export type ChangeboardingProduct = 'whiteboards' | 'databases';

export type ChangeboardingFilter = <T extends { feature: string }>(messages: T[]) => T[];

const DB_FEATURE_PREFIX = 'db';

const PRODUCT_FILTERS: Record<ChangeboardingProduct, ChangeboardingFilter> = {
  whiteboards: (messages) =>
    messages.filter((m) => !m.feature.startsWith(DB_FEATURE_PREFIX)),
  databases: (messages) =>
    messages.filter((m) => m.feature.startsWith(DB_FEATURE_PREFIX)),
};

const ChangeboardingFilterContext = createContext<ChangeboardingFilter>(
  PRODUCT_FILTERS.whiteboards
);

export const useChangeboardingFilter = (): ChangeboardingFilter =>
  useContext(ChangeboardingFilterContext);

const DEFAULT_CONTEXT = {
  locale: 'en',
  product: 'confluence-whiteboards',
};

// Base url where we gather the latest, correct shape of an eligible message.
// Because we are effectively using base/default context for an example message
// and simply tweaking the instance ID to be per-feature and thereby give us a
// unique identifier per feature, we should fetch from API to align all other
// properties with the latest post office model.
export const PATHNAME = '/gateway/api/post-office/api/v1/placements/';

const usePostOfficeContext = (): PostOfficeContextConfig => {
  const { id: orgId } = useSnapshot(currentOrgData);
  const { siteId: tenantId } = useSnapshot(currentSiteData);
  const locale = useUserLocale();

  const context = useMemo(
    () => ({
      current: {
        ...DEFAULT_CONTEXT,
        orgId,
        tenantId,
        locale,
        // TODO: consider isSiteAdmin, workspaceAri, workspaceId
        // isSiteAdmin: false,
      } satisfies PostOfficeContextConfig['current'],
    }),
    [orgId, tenantId, locale]
  );

  return context;
};

const usePostOfficeEnvironment = (): PostOfficeEnvironmentConfig => {
  // Keeping it only to a testing environment for now. Will memoise later if we
  // have any dependencies on changing variables.
  const env = useMemo((): Env => {
    const canvasEnv = process.env.VITE_CANVAS_ENV ?? process.env.MODE;
    switch (canvasEnv) {
      case 'staging':
      case 'stg-east':
        return 'staging';
      case 'prod':
      case 'prod-east':
        return 'production';
      default:
        return 'test-server';
    }
  }, []);

  return {
    env,
    overrides: {
      api: {
        // Ensures that the RML is pointing to the right place. Does not need to
        // include the instance ID in here.
        pathname: PATHNAME,
      },
    },
  };
};

export const PostOfficeProvider = ({
  children,
  product,
}: {
  children: React.ReactNode;
  product?: ChangeboardingProduct;
}) => {
  const context = usePostOfficeContext();
  const environment = usePostOfficeEnvironment();
  const filter = PRODUCT_FILTERS[product ?? 'whiteboards'];

  return (
    <ChangeboardingFilterContext.Provider value={filter}>
      <PostOfficeProviderBase context={context} environment={environment}>
        {children}
      </PostOfficeProviderBase>
    </ChangeboardingFilterContext.Provider>
  );
};
